import { useState, useEffect, useMemo } from 'react'
import { useApi } from '@/hooks/useApi'
import { useCachedData } from '@/hooks/useCachedData'
import { useRealtimeEvent } from '@/hooks/useRealtimeEvents'
import { usePagination } from '@/hooks/usePagination'
import type { BuzonHilo, BuzonRemitente } from '@/lib/types'
import { PageHeader, Pagination, Button } from '@/components/ui'
import { IconMail, IconSearch, IconSend } from '@/components/icons/Icons'
import CorreoListaItem from '@/components/correos/CorreoListaItem'
import BuzonHiloPanel from '@/components/correos/BuzonHilo'
import NuevoCorreoBuzon from '@/components/correos/NuevoCorreoBuzon'
import { etiquetaHilo } from '@/lib/correo-utils'

/*
  Buzón de Equipo: la bandeja de correo del equipo, dentro del ERP.

  Hasta ahora todo correo a un cliente salía del Gmail de José. Acá cada
  persona del equipo tiene su propio remitente sobre team.1conecta.com, con su
  firma, y el hilo queda guardado en el ERP.

  Layout: los dos paneles que ya usa la bandeja de las fichas
  (.correos-shell / .correos-lista / .correos-hilo). Ver
  prd/modulos/buzon/01-Buzon-Equipo.md
*/
type Direccion = 'todos' | 'entrante' | 'saliente'

export default function Buzon() {
  const api = useApi()
  const [abierto, setAbierto] = useState<number | null>(null)
  const [detalle, setDetalle] = useState<Record<number, BuzonHilo>>({})
  const [showNuevo, setShowNuevo] = useState(false)

  // Filtros: lo que pidió el usuario y nada más.
  const [remitenteId, setRemitenteId] = useState<number | 'todos'>('todos')
  const [direccion, setDireccion] = useState<Direccion>('todos')
  const [q, setQ] = useState('')

  // Los remitentes cambian cuando alguien los da de alta en Configuración, o
  // sea casi nunca: van con el caché normal de cinco minutos.
  const { data: remitentes = [] } = useCachedData(
    ['buzon-remitentes'],
    () => api.get<BuzonRemitente[]>('/api/buzon/remitentes').catch(err => {
      console.error('Error cargando remitentes:', err)
      return [] as BuzonRemitente[]
    }),
  )

  // Se espera un momento antes de pedir: así escribir en el buscador no
  // dispara una consulta por tecla. El término ya esperado es el que entra en
  // la clave del caché.
  const [qBuscado, setQBuscado] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setQBuscado(q.trim()), q ? 300 : 0)
    return () => clearTimeout(t)
  }, [q])

  // Treinta segundos y no cinco minutos: es una bandeja de correo, y el SSE del
  // webhook de entrada igual la refresca al llegar algo.
  // El filtrado va al backend —la bandeja crece sin techo y traerla entera para
  // filtrar en el navegador era el "Cargando…" de cada visita— así que cada
  // combinación de filtros es su propia entrada del caché.
  const { data: hilos = [], isLoading: loading, refrescar: cargar, actualizar } = useCachedData(
    ['buzon-hilos', remitenteId, direccion, qBuscado],
    () => {
      const p = new URLSearchParams()
      if (remitenteId !== 'todos') p.set('remitente_id', String(remitenteId))
      if (direccion !== 'todos') p.set('direccion', direccion)
      if (qBuscado) p.set('q', qBuscado)
      const qs = p.toString()
      return api.get<BuzonHilo[]>(`/api/buzon/hilos${qs ? `?${qs}` : ''}`).catch(err => {
        console.error('Error cargando el buzón:', err)
        return [] as BuzonHilo[]
      })
    },
    { staleTime: 30 * 1000 },
  )

  // Llega un correo nuevo: la bandeja se actualiza sola. El backend emite este
  // evento desde el webhook de entrada (fase de recepción).
  useRealtimeEvent('buzon_mensaje', (data: { hilo_id?: number }) => {
    cargar()
    if (data?.hilo_id) {
      setDetalle((prev) => { const n = { ...prev }; delete n[data.hilo_id!]; return n })
    }
  })

  const resetKey = useMemo(() => `${remitenteId}|${direccion}|${q}`, [remitenteId, direccion, q])
  const { page, setPage, totalPages, paginated, pageSize, total } = usePagination(hilos, 15, resetKey)

  // Memoizado: si se filtrara inline, el array cambiaría de identidad en cada
  // render y el efecto que arma el formulario de «Escribir» volvería a correr,
  // borrando lo que la persona está escribiendo en cuanto llegue un correo.
  const activos = useMemo(() => remitentes.filter((r) => r.activo), [remitentes])

  const sinLeer = useMemo(() => hilos.reduce((n, h) => n + (h.no_leidos || 0), 0), [hilos])
  const activo = hilos.find((h) => h.id === abierto) || null
  const hayFiltro = remitenteId !== 'todos' || direccion !== 'todos' || !!q.trim()

  async function abrir(h: BuzonHilo) {
    setAbierto(h.id)
    if (!detalle[h.id]) {
      try {
        const full = await api.get<BuzonHilo>(`/api/buzon/hilos/${h.id}`)
        setDetalle((prev) => ({ ...prev, [h.id]: full }))
      } catch (err) {
        console.error('Error abriendo el hilo:', err)
      }
    }
    if (h.no_leidos > 0) {
      try {
        await api.put(`/api/buzon/hilos/${h.id}/leido`, {})
        actualizar((prev) => prev.map((x) => (x.id === h.id ? { ...x, no_leidos: 0 } : x)))
      } catch (err) {
        console.error('Error marcando como leído:', err)
      }
    }
  }

  async function recargarHilo(id: number) {
    setDetalle((prev) => { const n = { ...prev }; delete n[id]; return n })
    await cargar()
    try {
      const full = await api.get<BuzonHilo>(`/api/buzon/hilos/${id}`)
      setDetalle((prev) => ({ ...prev, [id]: full }))
    } catch (err) {
      console.error('Error recargando el hilo:', err)
    }
  }

  function limpiar() {
    setRemitenteId('todos')
    setDireccion('todos')
    setQ('')
  }

  return (
    <div className="page-shell">
      <PageHeader
        breadcrumbs={[{ label: 'Buzón' }]}
        meta={loading ? 'Cargando…' : `${total} ${total === 1 ? 'hilo' : 'hilos'}${sinLeer ? ` · ${sinLeer} sin leer` : ''}`}
        actions={
          <Button onClick={() => setShowNuevo(true)} disabled={activos.length === 0}>
            <IconSend size={14} /> Escribir
          </Button>
        }
      />

      <div className="filters-bar">
        <div className="filters-bar__search">
          <IconSearch size={14} className="filters-bar__search-icon" />
          <input
            type="text"
            className="form-input"
            placeholder="Buscar por asunto, participante o texto…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        <select
          className="form-select"
          value={String(remitenteId)}
          onChange={(e) => setRemitenteId(e.target.value === 'todos' ? 'todos' : Number(e.target.value))}
        >
          <option value="todos">Todos los remitentes</option>
          {remitentes.map((r) => (
            <option key={r.id} value={r.id}>
              {r.nombre_mostrar}{r.activo ? '' : ' (inactivo)'}
            </option>
          ))}
        </select>

        <select
          className="form-select"
          value={direccion}
          onChange={(e) => setDireccion(e.target.value as Direccion)}
        >
          <option value="todos">Enviados y recibidos</option>
          <option value="entrante">Recibidos</option>
          <option value="saliente">Enviados</option>
        </select>

        {hayFiltro && (
          <button className="btn btn-secondary btn-sm filters-bar__clear" onClick={limpiar}>
            Limpiar filtros
          </button>
        )}
      </div>

      {!loading && hilos.length === 0 ? (
        <div className="card" style={{ padding: 28, textAlign: 'center' }}>
          <IconMail size={28} />
          <h3 style={{ marginTop: 10 }}>
            {hayFiltro ? 'Ningún hilo coincide con el filtro' : 'El buzón está vacío'}
          </h3>
          <p className="text-muted" style={{ marginTop: 6, marginBottom: 16 }}>
            {hayFiltro
              ? 'Probá quitando algún filtro.'
              : remitentes.length === 0
                ? 'Todavía no hay remitentes cargados. Se dan de alta en Configuración → Buzón.'
                : 'Escribile a alguien y el hilo aparece acá.'}
          </p>
          {hayFiltro && (
            <button className="btn btn-secondary" onClick={limpiar}>Limpiar filtros</button>
          )}
        </div>
      ) : (
        <>
          <div className={`correos-shell${activo ? ' is-leyendo' : ''}`}>
            <div className="correos-lista">
              {loading ? (
                <p className="text-muted" style={{ padding: 16, fontSize: 14 }}>Cargando…</p>
              ) : paginated.map((h) => (
                <CorreoListaItem
                  key={h.id}
                  // En el buzón "nuestro" dominio es el del remitente del hilo,
                  // no el del usuario conectado: el hilo es de Teresa aunque lo
                  // esté mirando otra persona del equipo.
                  quien={etiquetaHilo(h.participantes, h.de_ultimo, h.remitente_email)}
                  avatarDe={(h.participantes || []).find((p) => p !== h.remitente_email) || ''}
                  asunto={h.asunto}
                  resumen={h.resumen}
                  fecha={h.ultimo_mensaje_at}
                  adjuntos={h.adjuntos?.length ?? 0}
                  mensajes={h.cantidad ?? 0}
                  noLeidos={h.no_leidos}
                  activo={h.id === abierto}
                  onClick={() => abrir(h)}
                />
              ))}
            </div>

            {activo ? (
              <BuzonHiloPanel
                hilo={activo}
                detalle={detalle[activo.id] || null}
                onVolver={() => setAbierto(null)}
                onRespondido={() => recargarHilo(activo.id)}
              />
            ) : (
              <div className="correos-hilo">
                <div className="correos-vacio">
                  <IconMail size={26} />
                  <p>Elegí un hilo de la izquierda para leerlo.</p>
                </div>
              </div>
            )}
          </div>

          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPage={setPage}
            noun="hilos"
          />
        </>
      )}

      <NuevoCorreoBuzon
        open={showNuevo}
        remitentes={activos}
        onClose={() => setShowNuevo(false)}
        onEnviado={(hiloId) => { cargar(); setAbierto(hiloId) }}
      />
    </div>
  )
}
